// Shared utilities for mention functionality
// This file breaks the circular dependency between Markdown and Input components
export const MENTION_TRIGGER = '@';
export const MENTION_END_CHAR_REGEX = /\s|[,;(){}?]/;
export const MENTION_PROPERTIES_SEPARATOR = ':';
/**
 * Decode an entity id for display / composer storage (BE may send URI-encoded ids).
 */ export function decodeMentionEntityIdForDisplay(id) {
    try {
        return decodeURI(id);
    } catch  {
        return id;
    }
}
/**
 * Encode an entity id for Copilot BE payloads (spaces → %20, etc.). Apply only to the id segment.
 */ export function encodeMentionEntityIdForBE(id) {
    try {
        return encodeURI(id);
    } catch  {
        return id;
    }
}
function tryParseMentionPropertiesJsonFromBase64(b64) {
    try {
        const decoded = atob(b64);
        const properties = JSON.parse(decoded);
        if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
            return properties;
        }
    } catch  {
    // ignore
    }
    return undefined;
}
/**
 * Split `entityType:entityId[:base64Props]` where base64 is JSON mention properties.
 * The entity id may contain ':' and spaces; the suffix is detected by valid base64 JSON at the last colon run.
 */ export function splitMentionForParse(mentionText) {
    const firstColon = mentionText.indexOf(MENTION_PROPERTIES_SEPARATOR);
    if (firstColon <= 0) {
        return null;
    }
    const entityType = mentionText.slice(0, firstColon);
    const rest = mentionText.slice(firstColon + 1);
    if (!rest) {
        return null;
    }
    let idx = rest.lastIndexOf(MENTION_PROPERTIES_SEPARATOR);
    while(idx !== -1){
        const idPart = rest.slice(0, idx);
        const tail = rest.slice(idx + 1);
        const base64Match = tail.match(/^([A-Za-z0-9+/]+=*)/);
        const candidate = base64Match?.[1];
        if (candidate && tryParseMentionPropertiesJsonFromBase64(candidate)) {
            return {
                entityType,
                entityIdRaw: idPart,
                propertiesBase64: candidate
            };
        }
        idx = rest.lastIndexOf(MENTION_PROPERTIES_SEPARATOR, idx - 1);
    }
    return {
        entityType,
        entityIdRaw: rest
    };
}
/**
 * Entity types that default to 'name' display (showing last segment after dot).
 */ const NAME_DISPLAY_ENTITY_TYPES = [
    'dataset',
    'pipeline'
];
function getDefaultDisplayMode(entityType) {
    return NAME_DISPLAY_ENTITY_TYPES.includes(entityType.toLowerCase()) ? 'name' : 'path';
}
/**
 * Extract display text from an entity ID based on display mode.
 * - 'path': Returns the full ID as-is.
 * - 'name': If the ID contains dots (e.g., database.schema.table), returns the last segment.
 */ function getDisplayTextFromEntityId(entityId, display) {
    if (display === 'name' && entityId.includes('.')) {
        const parts = entityId.split('.');
        return parts[parts.length - 1];
    }
    return entityId;
}
/**
 * Parse a mention string that may include base64-encoded properties.
 * Format: entityType:entityId or entityType:entityId:base64EncodedProperties
 * Example: gem:Transform_1:eyJwaXBlbGluZSI6InAxIn0=
 */ export function parseMentionWithProperties(mentionText) {
    const split = splitMentionForParse(mentionText);
    if (!split) {
        return null;
    }
    const { entityType, entityIdRaw, propertiesBase64 } = split;
    const entityId = decodeMentionEntityIdForDisplay(entityIdRaw);
    let properties;
    if (propertiesBase64) {
        properties = tryParseMentionPropertiesJsonFromBase64(propertiesBase64);
    }
    const displayMode = properties?.display ?? getDefaultDisplayMode(entityType);
    const displayText = getDisplayTextFromEntityId(entityId, displayMode);
    const isShortened = displayText !== entityId;
    let mergedProperties = properties;
    if (isShortened && !mergedProperties?.fullPath) {
        mergedProperties = {
            ...mergedProperties,
            fullPath: entityId
        };
    }
    return {
        entityType,
        entityId,
        displayText,
        properties: mergedProperties,
        entityName: displayText.trimEnd()
    };
}
/**
 * Enrich a mention string with additional properties.
 * Parses existing properties (if any), merges with new properties, and serializes merged props to base64 when non-empty.
 * Entity id is written in decoded form (plain spaces); URI-encoding for BE is applied at send time.
 * @param mentionText - The mention string (without @ prefix), e.g., "gem:Transform_1" or "gem:Transform_1:base64props"
 * @param additionalProperties - Properties to merge into the mention
 * @returns The enriched mention string
 */ export function enrichMentionWithProperties(mentionText, additionalProperties) {
    const parsed = parseMentionWithProperties(mentionText);
    if (!parsed) {
        return mentionText;
    }
    const { entityType, entityId, properties: existingProperties } = parsed;
    // These are inferred properties computed at render time - don't persist them
    const { fullPath: _existingFullPath, category: _existingCategory, component: _existingComponent, processId: _existingProcessId, kind: _existingKind, ...cleanExistingProperties } = existingProperties || {};
    const finalProperties = {
        ...cleanExistingProperties,
        ...additionalProperties
    };
    const hasProperties = Object.keys(finalProperties).length > 0;
    let enrichedMention = `${entityType}${MENTION_PROPERTIES_SEPARATOR}${entityId}`;
    if (hasProperties) {
        const propertiesBase64 = btoa(JSON.stringify(finalProperties));
        enrichedMention += `${MENTION_PROPERTIES_SEPARATOR}${propertiesBase64}`;
    }
    return enrichedMention;
}
/**
 * End index (exclusive) for scanning the segment after `type:` within `text`.
 * Caps before another @-mention and at line breaks so we do not scan the rest of the prompt.
 * First generic whitespace is not a cap: entity ids may contain spaces before `:base64`.
 */ function indexOfMentionBodyScanBound(text, restStart) {
    const n = text.length;
    for(let i = restStart; i < n; i++){
        const ch = text[i];
        if (ch === '\n' || ch === '\r') {
            return i;
        }
        if (ch === MENTION_TRIGGER) {
            const atMentionStart = i === restStart || /\s/.test(text[i - 1]) || MENTION_END_CHAR_REGEX.test(text[i - 1]);
            if (atMentionStart) {
                return i;
            }
        }
    }
    return n;
}
function tryGetMentionValueWithPropertiesSuffix(text, startIndex) {
    const typeEnd = text.indexOf(MENTION_PROPERTIES_SEPARATOR, startIndex);
    if (typeEnd === -1) {
        return null;
    }
    const entityType = text.slice(startIndex, typeEnd);
    if (!entityType) {
        return null;
    }
    const restEnd = indexOfMentionBodyScanBound(text, typeEnd + 1);
    const rest = text.slice(typeEnd + 1, restEnd);
    if (!rest) {
        return null;
    }
    let idx = rest.lastIndexOf(MENTION_PROPERTIES_SEPARATOR);
    while(idx !== -1){
        const idPart = rest.slice(0, idx);
        const tail = rest.slice(idx + 1);
        const base64Match = tail.match(/^([A-Za-z0-9+/]+=*)/);
        const candidate = base64Match?.[1];
        if (candidate && tryParseMentionPropertiesJsonFromBase64(candidate)) {
            const mentionValue = `${entityType}${MENTION_PROPERTIES_SEPARATOR}${idPart}${MENTION_PROPERTIES_SEPARATOR}${candidate}`;
            const endIndex = startIndex + mentionValue.length;
            return {
                value: mentionValue,
                endIndex
            };
        }
        idx = rest.lastIndexOf(MENTION_PROPERTIES_SEPARATOR, idx - 1);
    }
    return null;
}
/**
 * Given a text and a start index, return the mention value and the end index of the first mention
 */ export function getMentionValue(text, startIndex) {
    const withProps = tryGetMentionValueWithPropertiesSuffix(text, startIndex);
    if (withProps) {
        return withProps;
    }
    let inQuote = false;
    let quoteChar = '';
    let mentionValue = '';
    let j = startIndex;
    // Process characters until we hit a space (outside quotes) or end of text
    while(j < text.length){
        // Handle quote characters
        if (!inQuote && (text[j] === "'" || text[j] === '"' || text[j] === '`')) {
            inQuote = true;
            quoteChar = text[j];
            mentionValue += text[j];
            j++;
            continue;
        }
        // Handle end of quote
        if (inQuote && text[j] === quoteChar) {
            inQuote = false;
            mentionValue += text[j];
            j++;
            continue;
        }
        // If we're outside quotes and hit a space or punctuation character (except . and :)
        //  or brackets (except the square branches, which is used for index), we're done
        if (!inQuote && MENTION_END_CHAR_REGEX.test(text[j])) {
            break;
        }
        // ignore trailing dots
        if (text[j] === '.' && (j === text.length - 1 || text[j + 1] === ' ')) {
            break;
        }
        // Add the character to our mention value
        mentionValue += text[j];
        j++;
    }
    return {
        value: mentionValue,
        endIndex: j
    };
}
/**
 * Same @-mention scan as `findMentions` in Mention/mentionUtils (uses {@link getMentionValue}).
 * Kept private here so {@link encodeMentionsInPromptForBE} does not import Mention/ (circular: Mention → Markdown).
 */ function scanMentionSpansForEncoding(text) {
    const mentions = [];
    for(let i = 0; i < text.length; i++){
        // Look for the trigger character
        if (text[i] === MENTION_TRIGGER && (i === 0 || /\s/.test(text[i - 1]) || MENTION_END_CHAR_REGEX.test(text[i - 1]))) {
            const start = i;
            const { value, endIndex } = getMentionValue(text, i + 1);
            const mentionValue = MENTION_TRIGGER + value;
            // If we found a valid mention (more than just the @ symbol)
            if (mentionValue.length > 1) {
                mentions.push({
                    start,
                    end: endIndex,
                    text: mentionValue
                });
                // Move i to the end of the mention - 1 (the loop will increment i)
                i = endIndex - 1;
            }
        }
    }
    return mentions;
}
/** Rebuild mention body with URI-encoded entity id; preserves the original base64 properties segment. */ function buildMentionBodyForBE(split) {
    const idForBE = encodeMentionEntityIdForBE(decodeMentionEntityIdForDisplay(split.entityIdRaw));
    let out = `${split.entityType}${MENTION_PROPERTIES_SEPARATOR}${idForBE}`;
    if (split.propertiesBase64) {
        out += `${MENTION_PROPERTIES_SEPARATOR}${split.propertiesBase64}`;
    }
    return out;
}
function mentionBodyForBEPayload(mentionBodyWithoutAt) {
    const split = splitMentionForParse(mentionBodyWithoutAt);
    return split ? buildMentionBodyForBE(split) : mentionBodyWithoutAt;
}
/**
 * Encode entity ids inside @mentions for the Copilot BE (composer may use plain spaces).
 * Replaces from right to left so indices stay valid.
 */ export function encodeMentionsInPromptForBE(prompt) {
    const matches = scanMentionSpansForEncoding(prompt);
    if (matches.length === 0) {
        return prompt;
    }
    let result = '';
    let last = 0;
    for (const m of matches){
        result += prompt.slice(last, m.start);
        const body = m.text.slice(1);
        result += MENTION_TRIGGER + mentionBodyForBEPayload(body);
        last = m.end;
    }
    result += prompt.slice(last);
    return result;
}
/**
 * Encode `@entityType:entityId` keys (e.g. Copilot context maps) for BE payloads.
 */ export function encodeMentionTriggerKeyForBE(key) {
    if (!key.startsWith(MENTION_TRIGGER)) {
        return key;
    }
    const split = splitMentionForParse(key.slice(MENTION_TRIGGER.length));
    return split ? MENTION_TRIGGER + buildMentionBodyForBE(split) : key;
}
/**
 * Apply {@link encodeMentionTriggerKeyForBE} to every key (Copilot context / mentions payloads).
 */ export function encodeCopilotAgentContextRecordKeysForBE(context) {
    const out = {};
    for (const [k, v] of Object.entries(context)){
        out[encodeMentionTriggerKeyForBE(k)] = v;
    }
    return out;
}
